import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import { findProductById, computeLivePrice, getCoefficient, getEffectiveMinQty } from '../lib/catalog';
import { useAuth } from '../context/AuthContext';
import { PlatformLogo } from '../components/PlatformLogo';
import { toast } from '../hooks/use-toast';
import { Lock, ShieldCheck, ArrowLeft, CreditCard, Sparkles, Zap, Crown, Mail } from 'lucide-react';

const Checkout = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const product = useMemo(() => findProductById(productId), [productId]);
  const initialQty = Number(searchParams.get('qty')) || (product ? Number(product.min) : 0);
  const [qty, setQty] = useState(initialQty);
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [loyalty, setLoyalty] = useState(null); // { eligible, cumulative_eur, amount_to_unlock_eur, discount_percent }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (product && !initialQty) setQty(Number(product.min || 0));
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced loyalty lookup when a valid email is entered.
  useEffect(() => {
    const e = (email || '').trim().toLowerCase();
    if (!e.includes('@') || e.length < 5) { setLoyalty(null); return; }
    const backend = process.env.REACT_APP_BACKEND_URL;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${backend}/api/loyalty/status?email=${encodeURIComponent(e)}`);
        if (res.ok) setLoyalty(await res.json());
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [email]);

  if (!product) {
    return (
      <div>
        <AnnouncementBar />
        <Header />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="glass-card rounded-3xl p-8">
            <div className="text-white text-lg font-semibold mb-2">Service introuvable</div>
            <p className="text-white/60 text-sm mb-6">Ce service n’existe pas ou a été retiré.</p>
            <Link to="/collections" className="btn-primary inline-flex items-center gap-2 text-sm">Retour au catalogue</Link>
          </div>
        </div>
      </div>
    );
  }

  const platform = product.platform;
  const rate = Number(product.rate || 0);
  const smmMax = Number(product.max || 1000000);
  const minQ = Math.min(smmMax, getEffectiveMinQty(product));
  const maxQ = smmMax;
  const coef = getCoefficient(product.platformKey);
  const baseTotal = computeLivePrice(rate, qty, coef);
  const total = (loyalty && loyalty.eligible)
    ? Math.max(1.0, Math.round(baseTotal * (1 - loyalty.discount_percent / 100) * 100) / 100)
    : baseTotal;
  const qtyValid = qty >= minQ && qty <= maxQ && Number.isFinite(qty);
  const handleValid = handle.trim().length >= 2;
  const canPay = qtyValid && handleValid && !submitting;

  const handlePay = async () => {
    if (!canPay) {
      if (!qtyValid) toast({ title: 'Quantité invalide', description: `Entrez une quantité entre ${minQ.toLocaleString('fr-FR')} et ${maxQ.toLocaleString('fr-FR')}.` });
      else if (!handleValid) toast({ title: 'Lien requis', description: 'Renseignez votre @pseudo ou le lien de votre publication.' });
      return;
    }
    if (!product.smmId) {
      toast({ title: 'Service indisponible', description: "Ce service n'est pas encore relié au fournisseur. Essayez un autre service." });
      return;
    }
    setSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smm_service_id: product.smmId,
          qty,
          handle: handle.trim(),
          platform_key: product.platformKey,
          product_name: product.name,
          origin_url: window.location.origin,
          user_id: user?.id || null,
          customer_email: (email || '').trim().toLowerCase() || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Paiement impossible', description: err.detail || 'Une erreur est survenue.' });
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      // Redirige vers Stripe Checkout hébergé
      window.location.href = data.checkout_url;
    } catch (e) {
      toast({ title: 'Erreur réseau', description: "Impossible de contacter le serveur de paiement." });
      setSubmitting(false);
    }
  };

  const chips = [minQ, Math.max(minQ, 1000), Math.max(minQ, 5000), Math.min(maxQ, 10000)]
    .filter((v, i, a) => v <= maxQ && a.indexOf(v) === i)
    .slice(0, 4);

  return (
    <div className="min-h-screen overflow-x-hidden pb-32 md:pb-14">
      <AnnouncementBar />
      <Header />

      <div className="max-w-lg mx-auto px-4 sm:px-6 pt-6 pb-6">
        <button onClick={() => navigate(-1)} data-testid="checkout-back-btn"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 transition">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="text-[#ff8247] text-[10px] font-bold uppercase tracking-widest mb-2">Paiement sécurisé</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Finalisez votre <span className="gradient-text">boost</span>
        </h1>

        {/* Résumé produit */}
        <div className="glass-card rounded-2xl p-4 mb-4" data-testid="checkout-product-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${platform?.color}20` }}>
              <PlatformLogo platform={platform?.id} className="w-7 h-7" color={platform?.id === 'instagram' ? undefined : platform?.color} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white/50 text-[10px] font-bold uppercase tracking-widest truncate">{platform?.name}</div>
              <div className="text-white font-semibold text-sm sm:text-base leading-tight truncate">{product.name}</div>
            </div>
          </div>
        </div>

        {/* Bloc quantité */}
        <div className="glass-card rounded-2xl p-4 mb-4">
          <label className="text-white/60 text-xs font-semibold mb-2 block">Quantité</label>
          <div className="relative">
            <input
              data-testid="checkout-qty-input"
              type="number"
              inputMode="numeric"
              value={qty || ''}
              onChange={e => setQty(parseInt(e.target.value) || 0)}
              min={minQ}
              max={maxQ}
              step="1"
              className={`w-full min-w-0 bg-white/5 border rounded-xl pl-4 pr-16 py-3.5 text-white text-xl font-bold focus:outline-none transition ${qtyValid ? 'border-white/10 focus:border-[#ff8247]' : 'border-red-500/60'}`}
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              placeholder={`${minQ}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">unités</div>
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px]">
            <span className="text-white/50">Min · <span className="text-white font-semibold">{minQ.toLocaleString('fr-FR')}</span></span>
            <span className="text-white/50">Max · <span className="text-white font-semibold">{maxQ.toLocaleString('fr-FR')}</span></span>
          </div>
          {!qtyValid && qty > 0 && (
            <div className="mt-2 text-red-400 text-xs font-semibold">
              {qty < minQ ? `Minimum : ${minQ.toLocaleString('fr-FR')} unités` : `Maximum : ${maxQ.toLocaleString('fr-FR')} unités`}
            </div>
          )}
          <div className="grid grid-cols-4 gap-2 mt-3">
            {chips.map(v => (
              <button key={v} onClick={() => setQty(v)} data-testid={`checkout-qty-chip-${v}`}
                className="py-2 rounded-lg bg-white/5 hover:bg-[#ff8247]/10 border border-white/10 hover:border-[#ff8247]/30 text-white/80 text-[11px] font-semibold transition truncate">
                {v.toLocaleString('fr-FR')}
              </button>
            ))}
          </div>
        </div>

        {/* Lien / pseudo */}
        <div className="glass-card rounded-2xl p-4 mb-4">
          <label className="text-white/60 text-xs font-semibold mb-2 block">Votre @pseudo ou lien</label>
          <input
            data-testid="checkout-handle-input"
            value={handle}
            onChange={e => setHandle(e.target.value)}
            placeholder={platform?.id === 'google' ? 'Lien Google Business' : '@votrepseudo ou https://…'}
            autoComplete="off"
            className={`w-full min-w-0 bg-white/5 border rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 text-sm focus:outline-none transition ${handle && !handleValid ? 'border-red-500/60' : 'border-white/10 focus:border-[#ff8247]'}`}
          />
          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-white/50">
            <ShieldCheck className="w-3.5 h-3.5 text-[#00b67a] flex-shrink-0" />
            <span>Aucun mot de passe demandé, jamais.</span>
          </div>
        </div>

        {/* Email + fidélité */}
        <div className="glass-card rounded-2xl p-4 mb-4">
          <label className="text-white/60 text-xs font-semibold mb-2 block">Votre email <span className="text-white/40 font-normal">· pour le suivi de commande</span></label>
          <div className="relative">
            <Mail className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="checkout-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              autoComplete="email"
              className="w-full min-w-0 bg-white/5 border rounded-xl pl-11 pr-4 py-3.5 text-white placeholder:text-white/30 text-sm focus:outline-none transition border-white/10 focus:border-[#ff8247]"
            />
          </div>
          {loyalty && loyalty.eligible && (
            <div data-testid="loyalty-eligible-badge" className="mt-3 flex items-center gap-2 rounded-xl bg-[#00b67a]/10 border border-[#00b67a]/30 px-3 py-2">
              <Crown className="w-4 h-4 text-[#00b67a] flex-shrink-0" />
              <div className="text-[#00b67a] text-xs font-semibold">Fidélité VIP · −{loyalty.discount_percent}% appliqué automatiquement</div>
            </div>
          )}
          {loyalty && !loyalty.eligible && loyalty.cumulative_eur > 0 && (
            <div data-testid="loyalty-progress" className="mt-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2">
              <div className="text-white/70 text-[11px] mb-1">
                <Crown className="inline w-3 h-3 text-[#ff8247] mr-1" />
                Encore <span className="text-white font-bold">{loyalty.amount_to_unlock_eur.toFixed(2)} €</span> pour débloquer −10% à vie
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#ff8247] to-[#ff2d6f]" style={{ width: `${Math.min(100, (loyalty.cumulative_eur / loyalty.threshold_eur) * 100)}%` }} />
              </div>
              <div className="text-white/40 text-[10px] mt-1">{loyalty.cumulative_eur.toFixed(2)} € / {loyalty.threshold_eur.toFixed(2)} € cumulés</div>
            </div>
          )}
        </div>

        {/* Récapitulatif */}
        <div className="glass-card rounded-2xl p-4 mb-4">
          <div className="text-white font-bold text-sm mb-3">Récapitulatif</div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/60 truncate">Service</span>
              <span className="text-white font-semibold text-right truncate max-w-[60%]">{product.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Quantité</span>
              <span className="text-white font-semibold">{qty.toLocaleString('fr-FR')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Livraison</span>
              <span className="text-[#00b67a] font-semibold">Offerte · 12h</span>
            </div>
            {loyalty && loyalty.eligible && (
              <div className="flex items-center justify-between">
                <span className="text-[#00b67a] font-semibold">Fidélité VIP</span>
                <span className="text-[#00b67a] font-semibold">−{loyalty.discount_percent}%</span>
              </div>
            )}
          </div>
          <div className="h-px bg-white/10 my-3" />
          <div className="flex items-end justify-between">
            <span className="text-white/80 text-sm">Total</span>
            <div className="text-right">
              {loyalty && loyalty.eligible && baseTotal > total && (
                <div className="text-white/40 text-sm line-through">{baseTotal.toFixed(2)} €</div>
              )}
              <span data-testid="checkout-total-price" className="text-white text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {total.toFixed(2)} <span className="text-lg text-white/60">€</span>
              </span>
            </div>
          </div>
        </div>

        {/* CTA payer (desktop) */}
        <div className="hidden md:block">
          <button onClick={handlePay} disabled={!canPay} data-testid="checkout-pay-btn"
            className="btn-primary w-full justify-center inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            <Lock className="w-4 h-4" /> {submitting ? 'Traitement…' : `Payer maintenant · ${total.toFixed(2)} €`}
          </button>
        </div>

        {/* Trust badges */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="glass-card rounded-xl p-3 flex flex-col items-center gap-1 text-center">
            <ShieldCheck className="w-4 h-4 text-[#ff8247]" />
            <div className="text-white text-[10px] font-semibold">Sécurisé</div>
            <div className="text-white/50 text-[9px]">Paiement SSL</div>
          </div>
          <div className="glass-card rounded-xl p-3 flex flex-col items-center gap-1 text-center">
            <Zap className="w-4 h-4 text-[#ff8247]" />
            <div className="text-white text-[10px] font-semibold">Livraison 12h</div>
            <div className="text-white/50 text-[9px]">Progressive</div>
          </div>
          <div className="glass-card rounded-xl p-3 flex flex-col items-center gap-1 text-center">
            <Sparkles className="w-4 h-4 text-[#ff8247]" />
            <div className="text-white text-[10px] font-semibold">Garantie</div>
            <div className="text-white/50 text-[9px]">Renvois offerts</div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-5 text-white/40 text-[11px]">
          <CreditCard className="w-3.5 h-3.5" />
          <span>Cartes acceptées · Visa · Mastercard · Apple Pay</span>
        </div>
      </div>

      {/* CTA sticky bottom (mobile) */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 z-30">
        <div className="mx-3 mb-3 rounded-2xl bg-[#0a0a0f]/95 backdrop-blur-xl border border-white/10 p-3 shadow-[0_-8px_32px_rgba(0,0,0,0.5)]">
          <button onClick={handlePay} disabled={!canPay} data-testid="checkout-pay-btn-mobile"
            className="btn-primary w-full justify-center inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '14px 20px' }}>
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{submitting ? 'Traitement…' : `Payer ${total.toFixed(2)} €`}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
